import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { tableReservationService, restaurantTableService } from '../../../../services/restaurantTableService';
import { ArrowLeft, BookOpen, AlertCircle, Zap } from 'lucide-react';
import { getServerErrorMessage } from '../../../../utils/errorUtils';
import '../RestaurantPage.css';

interface FormData {
  tableId:         number;
  customerName:    string;
  customerPhone:   string;
  partySize:       number;
  reservationDate: string;
  reservationTime: string;
  durationMinutes: number;
  notes:           string;
  status:          string;
}

const STATUSES = ['Pending', 'Confirmed', 'Seated', 'Completed', 'Cancelled'];

const ReservationCreatePage: React.FC = () => {
  const navigate   = useNavigate();
  const { id }     = useParams<{ id: string }>();
  const { t }      = useTranslation();
  const isEdit     = Boolean(id);

  const [saving,      setSaving]      = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [tables,      setTables]      = useState<any[]>([]);
  const [error,       setError]       = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [formData,    setFormData]    = useState<FormData>({
    tableId: 0, customerName: '', customerPhone: '',
    partySize: 1, reservationDate: new Date().toISOString().slice(0, 10),
    reservationTime: '12:00', durationMinutes: 90, notes: '', status: 'Pending',
  });

  useEffect(() => {
    const init = async () => {
      setLoadingData(true);
      try {
        const res = await restaurantTableService.getAll();
        const d = res?.data?.data;
        const arr = Array.isArray(d) ? d : (d as any)?.data ?? [];
        setTables(arr.filter((t: any) => t.isActive !== false));

        if (isEdit && id) {
          const r = await tableReservationService.getById(parseInt(id));
          const rv = (r.data as any)?.data ?? r.data;
          setFormData({
            tableId:         rv.tableId         ?? 0,
            customerName:    rv.customerName     ?? '',
            customerPhone:   rv.customerPhone    ?? '',
            partySize:       rv.partySize        ?? 1,
            reservationDate: rv.reservationDate  ?? new Date().toISOString().slice(0, 10),
            reservationTime: rv.reservationTime  ?? '12:00',
            durationMinutes: rv.durationMinutes  ?? 90,
            notes:           rv.notes            ?? '',
            status:          rv.status           ?? 'Pending',
          });
        }
      } catch {
        setError(t('reservation.failedToLoad', 'Failed to load data.'));
      } finally {
        setLoadingData(false);
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (field: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { value, type } = e.target;
      setFormData(p => ({ ...p, [field]: type === 'number' ? Number(value) : value }));
      setFieldErrors(p => ({ ...p, [field]: undefined }));
    };

  const validate = () => {
    const errs: Partial<Record<keyof FormData, string>> = {};
    if (!formData.tableId)             errs.tableId      = t('common.required');
    if (!formData.customerName.trim()) errs.customerName = t('common.required');
    if (formData.partySize < 1)        errs.partySize    = t('table.capacityMin', 'Min 1');
    if (!formData.reservationDate)     errs.reservationDate = t('common.required');
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true); setError(null);
    const payload = {
      tableId:         formData.tableId,
      customerName:    formData.customerName.trim(),
      customerPhone:   formData.customerPhone.trim() || undefined,
      partySize:       formData.partySize,
      reservationDate: formData.reservationDate,
      reservationTime: formData.reservationTime,
      durationMinutes: formData.durationMinutes,
      notes:           formData.notes.trim() || undefined,
      status:          formData.status,
    };
    try {
      if (isEdit && id) await tableReservationService.update(parseInt(id), payload);
      else              await tableReservationService.create(payload);
      navigate('/restaurant/reservations');
    } catch (err: any) {
      setError(getServerErrorMessage(err) ?? t('reservation.failedToSave', 'Failed to save reservation.'));
      setSaving(false);
    }
  };

  if (loadingData) {
    return (
      <Layout>
        <div className="ims-create-body-page rest-create-page" style={{ alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="rest-create-page">
        <div className="ims-create-body-page">

          <div className="ims-top-bar">
            <div className="ims-top-bar-left">
              <button type="button" className="ims-back-btn" onClick={() => navigate('/restaurant/reservations')}>
                <ArrowLeft size={13} /> {t('reservation.title')}
              </button>
              <div>
                <h1 className="ims-page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <BookOpen size={18} style={{ color: 'var(--ims-accent)' }} />
                  {isEdit ? t('reservation.editTitle', 'Edit Reservation') : t('reservation.createTitle', 'New Reservation')}
                </h1>
                <p className="ims-page-subtitle">
                  {isEdit ? t('reservation.editSubtitle', 'Update reservation details') : t('reservation.createSubtitle', 'Book a table for a customer')}
                </p>
              </div>
            </div>
            <div className="ims-top-bar-right">
              <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/restaurant/reservations')} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="ims-btn ims-btn-primary" disabled={saving}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" />{t('common.saving')}</>
                  : (isEdit ? t('common.update') : t('common.save'))}
              </button>
            </div>
          </div>

          {error && (
            <div className="ims-error-banner">
              <div className="ims-alert ims-alert-error" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={15} />
                <span style={{ flex: 1 }}>{error}</span>
                <button type="button" className="btn-close" onClick={() => setError(null)} />
              </div>
            </div>
          )}

          <div className="ims-create-body ims-create-body-single">
            <div className="ims-panel-center" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Table & Customer */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <BookOpen size={14} className="mi-header-icon" />
                  <span className="ims-card-title">{t('reservation.details', 'Reservation Details')}</span>
                </div>
                <div className="ims-card-body mi-card-body">

                  <div className={`mi-field${fieldErrors.tableId ? ' mi-field-error' : ''}`}>
                    <label className="mi-label">{t('table.title')} <span className="mi-required">*</span></label>
                    <select className="mi-input" value={formData.tableId} onChange={set('tableId')}>
                      <option value={0}>{t('reservation.selectTable', '— Select Table —')}</option>
                      {tables.map(tbl => (
                        <option key={tbl.id} value={tbl.id}>
                          {tbl.tableNumber} – {tbl.name} ({tbl.capacity} {t('reservation.seats', 'seats')})
                          {tbl.section ? ` [${tbl.section}]` : ''}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.tableId && <span className="mi-err">{fieldErrors.tableId}</span>}
                  </div>

                  <div className="mi-row-2">
                    <div className={`mi-field${fieldErrors.customerName ? ' mi-field-error' : ''}`}>
                      <label className="mi-label">{t('reservation.customerName')} <span className="mi-required">*</span></label>
                      <input type="text" className="mi-input" value={formData.customerName} onChange={set('customerName')}
                        placeholder={t('reservation.customerNamePlaceholder', 'Full name')} maxLength={200} autoFocus />
                      {fieldErrors.customerName && <span className="mi-err">{fieldErrors.customerName}</span>}
                    </div>
                    <div className="mi-field">
                      <label className="mi-label">{t('reservation.customerPhone')}</label>
                      <input type="tel" className="mi-input" value={formData.customerPhone} onChange={set('customerPhone')}
                        placeholder="+1 555 000 0000" maxLength={30} />
                    </div>
                  </div>

                  <div className={`mi-field${fieldErrors.partySize ? ' mi-field-error' : ''}`}>
                    <label className="mi-label">{t('reservation.partySize')}</label>
                    <input type="number" className="mi-input" value={formData.partySize} onChange={set('partySize')} min={1} max={100} />
                    {fieldErrors.partySize && <span className="mi-err">{fieldErrors.partySize}</span>}
                  </div>
                </div>
              </div>

              {/* Date & Time */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <Zap size={14} className="mi-header-icon" />
                  <span className="ims-card-title">{t('reservation.dateTime', 'Date & Time')}</span>
                </div>
                <div className="ims-card-body mi-card-body">

                  <div className="mi-row-2">
                    <div className={`mi-field${fieldErrors.reservationDate ? ' mi-field-error' : ''}`}>
                      <label className="mi-label">{t('reservation.date')} <span className="mi-required">*</span></label>
                      <input type="date" className="mi-input" value={formData.reservationDate} onChange={set('reservationDate')} />
                      {fieldErrors.reservationDate && <span className="mi-err">{fieldErrors.reservationDate}</span>}
                    </div>
                    <div className="mi-field">
                      <label className="mi-label">{t('reservation.time')}</label>
                      <input type="time" className="mi-input" value={formData.reservationTime} onChange={set('reservationTime')} />
                    </div>
                  </div>

                  <div className="mi-field">
                    <label className="mi-label">{t('reservation.durationMinutes', 'Duration (minutes)')}</label>
                    <input type="number" className="mi-input" value={formData.durationMinutes} onChange={set('durationMinutes')} min={15} max={480} step={15} />
                  </div>

                  {isEdit && (
                    <div className="mi-field">
                      <label className="mi-label">{t('common.status')}</label>
                      <select className="mi-input" value={formData.status} onChange={set('status')}>
                        {STATUSES.map(s => (
                          <option key={s} value={s}>{t(`reservation.statuses.${s}`, s)}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="mi-field">
                    <label className="mi-label">{t('reservation.notes', 'Notes')}</label>
                    <textarea className="mi-input mi-textarea" value={formData.notes} onChange={set('notes')}
                      rows={3} placeholder={t('reservation.notesPlaceholder', 'Special requests, dietary requirements…')} maxLength={500} />
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </form>
    </Layout>
  );
};

export default ReservationCreatePage;
